/**
 * WorkerTimeEntries — day-by-day attendance log with
 * scheduled-vs-actual diff and missing-punch action.
 *
 * Rewrite of the legacy entries-only table that silently dropped
 * days when the worker forgot to clock in. Now backed by
 * /v1/me/time-entries/daily which expands the date range into a
 * full day list, computes scheduled/actual diff per row, and flags
 * rows where the worker should submit a missed_clock_in approval.
 *
 * Layout:
 *   - Header: title + date-range presets (last 7d / 30d / month-to-
 *     date / custom) + raw start/end date pickers
 *   - Inline §13 stats row (on-time / late / absent / incomplete /
 *     total hours)
 *   - Day table: Date · Scheduled · Actual · Diff · Status · Action
 *   - 'Submit missed clock-in' Dialog (centered, svdy-ui §5) — wires
 *     into the same /v1/attendance/approvals endpoint admin uses
 *     via the on-behalf path; from the worker side we POST through
 *     a new /v1/me/missed-clock-in flow if/when added. For now the
 *     button surfaces a toast pointing the worker to the existing
 *     /me/requests/new flow with type=missed_clock_in pre-filled.
 *     (Was a right-side Drawer pre-2026-05-22; converted to Dialog
 *     to keep all worker-portal mutation surfaces consistent.)
 *
 * Mobile: table is kept at all widths in v1 — see svdy-ui §2:
 * Apple-compact is the bar, not Apple-mobile-card. Card-list collapse
 * deferred until SMB feedback explicitly asks; the table scrolls
 * horizontally on xs and that's acceptable for the first cut.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Avatar, Box, Button, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl,
  IconButton,
  InputLabel, Paper, Snackbar, Stack, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import {
  ErrorOutlineOutlined, PhotoCameraOutlined, ScheduleOutlined,
  LocationOnOutlined,
} from '@mui/icons-material';
import { ApiError, meApi } from '../../api';
import type { MePunch } from '../../api/me';
import type { DayEntry, TimeEntriesDailyResponse } from '../../api/me';
import PhotoViewerDialog from '../../components/PhotoViewerDialog';
import LocationViewerDialog, { type ViewerFence } from '../../components/location/LocationViewerDialog';
import { activeLocale, fmtLocalTime, fmtMinutesSpan, fmtUnit } from '../../utils/datetime';

import DateField from '../../components/inputs/DateField';
import { translateApiError } from '../../i18n/translateApiError';
type Preset = 'today' | 'week' | 'month' | 'custom';

function isoToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function isoDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function isoMonthStart(): string {
  const d = new Date();
  d.setDate(1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function presetRange(p: Preset): [string, string] {
  if (p === 'today') return [isoToday(), isoToday()];
  if (p === 'week') return [isoDaysAgo(6), isoToday()];       // last 7 days
  if (p === 'month') return [isoMonthStart(), isoToday()];    // this month
  return [isoDaysAgo(13), isoToday()];                        // custom seed = 14d
}

function fmtTime(iso: string | null): string {
  if (!iso) return '—';
  // ADR-028: backend now returns clock_in/out as org-local "HH:MM".
  // Still accept legacy ISO ("...THH:MM...") for safety.
  if (/^\d{2}:\d{2}$/.test(iso)) return iso;
  const m = iso.match(/T(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : '—';
}
function _fmtMinutes(m: number): string {
  if (m === 0) return '—';
  return fmtMinutesSpan(m);
}
function fmtHours(m: number): string {
  return fmtUnit(m / 60, 'hour');
}

/** Group raw punches by local day, newest day first, punches ascending within
 *  the day. Drives the range-based Clock records list. */
function groupPunchesByDay(
  punches: MePunch[],
): { day: string; label: string; items: MePunch[] }[] {
  const map = new Map<string, MePunch[]>();
  for (const p of punches) {
    const d = new Date(p.punched_at);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const arr = map.get(key);
    if (arr) arr.push(p); else map.set(key, [p]);
  }
  return Array.from(map.entries())
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([day, items]) => ({
      day,
      label: new Date(day + 'T00:00:00').toLocaleDateString(activeLocale(), {
        weekday: 'short', month: 'short', day: 'numeric',
      }),
      items: items.sort((a, b) => new Date(a.punched_at).getTime() - new Date(b.punched_at).getTime()),
    }));
}

function statusChip(status: DayEntry['status']):
  { labelKey: string; color: 'success' | 'warning' | 'error' | 'info' | 'default' } {
  switch (status) {
    case 'on_time':     return { labelKey: 'statusOnTime', color: 'success' };
    case 'late':        return { labelKey: 'statusLate', color: 'warning' };
    case 'early_leave': return { labelKey: 'statusEarlyLeave', color: 'warning' };
    case 'incomplete':  return { labelKey: 'statusIncomplete', color: 'error' };
    case 'absent':      return { labelKey: 'statusAbsent', color: 'error' };
    // Approved leave. Not 'error': the backend used to send `absent` for a day the
    // manager had approved, so the worker's own calendar showed a red no-show against
    // leave they had been granted. `info` matches how holiday reads — a day off with a
    // reason, not a problem to fix.
    case 'leave':       return { labelKey: 'statusLeave', color: 'info' };
    case 'rest':        return { labelKey: 'statusRest', color: 'default' };
    case 'holiday':     return { labelKey: 'statusHoliday', color: 'info' };
    case 'unscheduled': return { labelKey: 'statusUnscheduled', color: 'default' };
    case 'scheduled':   return { labelKey: 'statusScheduled', color: 'info' };
    default:            return { labelKey: '', color: 'default' };
  }
}

const WorkerTimeEntries: React.FC = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<TimeEntriesDailyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [preset, setPreset] = useState<Preset>('week');
  const [startDate, setStartDate] = useState<string>(presetRange('week')[0]);
  const [endDate, setEndDate] = useState<string>(presetRange('week')[1]);
  const [toast, setToast] = useState<string | null>(null);

  // Missed-punch drawer state
  const [missedDay, setMissedDay] = useState<DayEntry | null>(null);
  // Privacy audit Wave 5 — worker photo viewer state. GDPR Art. 15
  // right of access: workers can review the selfies captured at clock-in.
  const [photoDay, setPhotoDay] = useState<DayEntry | null>(null);
  const [punchPhotoUrl, setPunchPhotoUrl] = useState<string | null>(null);
  const [missedTime, setMissedTime] = useState<string>('');
  const [missedReason, setMissedReason] = useState<string>('');
  const [missedSubmitting, setMissedSubmitting] = useState(false);
  const [missedError, setMissedError] = useState<string | null>(null);

  // ADR-028 — Clock records (every raw punch) vs Timesheet (per-day summary).
  // Clock records is the default / left tab (owner 2026-06-26). BOTH views are
  // driven by the SAME date-range presets above — no separate single-day picker.
  const [view, setView] = useState<'events' | 'timesheet'>('events');
  const [punches, setPunches] = useState<MePunch[]>([]);
  const [punchesLoading, setPunchesLoading] = useState(false);

  // Location map popup (owner 2026-06-26 — punches were a "黑盒"). The
  // geofence circles come from the worker's resolved clock-in-config (the
  // same endpoint the Clock page uses); the marker is the punch's own coords.
  const [locPunch, setLocPunch] = useState<MePunch | null>(null);
  const [fences, setFences] = useState<ViewerFence[]>([]);
  useEffect(() => {
    let alive = true;
    meApi.getClockInConfig()
      .then((cfg) => {
        if (!alive) return;
        const fs = (cfg?.geofence?.fences ?? []).map((f) => ({
          name: f.name, lat: f.lat, lng: f.lng, radius_m: f.radius_m,
        }));
        setFences(fs);
      })
      .catch(() => { /* no fences → marker-only map */ });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (view !== 'events') return;
    let alive = true;
    setPunchesLoading(true);
    meApi.punchesRange(startDate, endDate)
      .then((rows) => { if (alive) setPunches(rows); })
      .catch(() => { if (alive) setPunches([]); })
      .finally(() => { if (alive) setPunchesLoading(false); });
    return () => { alive = false; };
  }, [view, startDate, endDate]);

  const fetchDaily = useCallback(async (s: string, e: string) => {
    setLoading(true);
    setError(null);
    try {
      const body = await meApi.timeEntries.daily(s, e);
      setData(body);
    } catch (err) {
      setError((err as Error).message ?? t('common:feedback.loadFailed'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchDaily(startDate, endDate);
  }, [startDate, endDate, fetchDaily]);

  const handlePreset = (p: Preset) => {
    setPreset(p);
    if (p !== 'custom') {
      const [s, e] = presetRange(p);
      setStartDate(s);
      setEndDate(e);
    }
  };

  const days = data?.days ?? [];

  const openMissed = (d: DayEntry) => {
    setMissedDay(d);
    setMissedTime(d.scheduled_in ?? '09:00');
    setMissedReason('');
    setMissedError(null);
  };
  const closeMissed = () => {
    if (missedSubmitting) return;
    setMissedDay(null);
  };

  /** Submit a missed_clock_in as an ATTENDANCE request (the same path mobile
   *  uses) — POST /me/attendance-requests with the type-specific payload. It
   *  lands in attendance approvals and, once approved, actually writes the
   *  correction punch + re-derives the day (backend _apply_missed_punch_
   *  decision). The old /me/requests process path did neither. */
  const submitMissed = async () => {
    if (!missedDay) return;
    if (!missedTime || !missedReason.trim()) {
      setMissedError(t('worker:timeEntries.fillError'));
      return;
    }
    setMissedSubmitting(true);
    setMissedError(null);
    try {
      await meApi.attendanceRequests.create({
        type: 'missed_clock_in',
        data: {
          date: missedDay.date,
          time: missedTime,
          reason: missedReason.trim(),
        },
      });
      setToast(t('worker:timeEntries.toastMissedSubmitted'));
      setMissedDay(null);
      // No refetch needed — the day status doesn't flip until
      // a manager approves the request and an admin manually
      // adds the time entry. The drawer just closes.
    } catch (e) {
      setMissedError(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.submitFailed'));
    } finally {
      setMissedSubmitting(false);
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      py: '12px',
      width: '100%',
    }}>
      {/* Header row */}
      <Box sx={{
        width: '100%', display: 'flex',
        justifyContent: 'space-between', alignItems: 'flex-end',
        mb: 1.5, flexWrap: 'wrap', gap: 1.5,
      }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
            {t('worker:timeEntries.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('worker:timeEntries.subtitle')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <ToggleButtonGroup
            value={preset}
            exclusive
            onChange={(_, v) => v && handlePreset(v)}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontSize: '0.75rem',
                py: 0.25,
                px: 1.25,
                borderColor: 'divider',
              },
            }}
          >
            <ToggleButton value="today">{t('worker:timeEntries.presetToday')}</ToggleButton>
            <ToggleButton value="week">{t('worker:timeEntries.presetWeek')}</ToggleButton>
            <ToggleButton value="month">{t('worker:timeEntries.presetMonth')}</ToggleButton>
            <ToggleButton value="custom">{t('worker:timeEntries.presetCustom')}</ToggleButton>
          </ToggleButtonGroup>
          {preset === 'custom' && (
            <>
              <DateField
                size="small"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                sx={{ width: 140 }}
              />
              <DateField
                size="small"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                sx={{ width: 140 }}
              />
            </>
          )}
        </Stack>
      </Box>

      {/* ADR-028 — Timesheet ↔ Clock events. At-a-glance, one tap. */}
      <ToggleButtonGroup
        value={view}
        exclusive
        onChange={(_, v) => v && setView(v)}
        size="small"
        sx={{
          mb: 2,
          '& .MuiToggleButton-root': {
            textTransform: 'none', fontSize: '0.8rem', py: 0.4, px: 2, borderColor: 'divider',
          },
        }}
      >
        <ToggleButton value="events">{t('worker:timeEntries.viewEvents', { defaultValue: 'Clock records' })}</ToggleButton>
        <ToggleButton value="timesheet">{t('worker:timeEntries.viewTimesheet', { defaultValue: 'Timesheet' })}</ToggleButton>
      </ToggleButtonGroup>

      {/* Inline stats §13 */}
      {view === 'timesheet' && data && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          <strong>{data.days_on_time}</strong> {t('worker:timeEntries.statOnTime')}
          {' · '}
          <strong>{data.days_late}</strong> {t('worker:timeEntries.statLateEarly')}
          {' · '}
          <strong>{data.days_absent}</strong> {t('worker:timeEntries.statAbsent')}
          {' · '}
          <strong>{data.days_incomplete}</strong> {t('worker:timeEntries.statIncomplete')}
          {' · '}
          <strong>{fmtHours(data.total_actual_minutes)}</strong> {t('worker:timeEntries.statActual')} /{' '}
          <strong>{fmtHours(data.total_scheduled_minutes)}</strong> {t('worker:timeEntries.statScheduled')}
        </Typography>
      )}

      {error && (
        <Paper elevation={0} sx={{
          width: '100%', p: 2, mb: 1.5,
          borderRadius: 2, border: '1px solid', borderColor: 'error.main',
        }}>
          <Typography variant="body2" color="error">{error}</Typography>
        </Paper>
      )}

      {view === 'timesheet' && loading && !data && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6, width: '100%' }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {view === 'timesheet' && data && (
        <TableContainer component={Paper} sx={{
          width: '100%', borderRadius: 2, overflow: 'hidden',
          border: '1px solid', borderColor: 'divider', boxShadow: 'none',
        }}>
          <Table size="small">
            <TableHead sx={{
              bgcolor: 'grey.100',
              '& .MuiTableCell-root': { fontWeight: 600, fontSize: '0.75rem' },
            }}>
              <TableRow>
                <TableCell>{t('worker:timeEntries.colDate')}</TableCell>
                <TableCell>{t('worker:timeEntries.colScheduled')}</TableCell>
                <TableCell>{t('worker:timeEntries.colActual')}</TableCell>
                <TableCell>{t('worker:timeEntries.colDiff')}</TableCell>
                <TableCell align="center">{t('worker:timeEntries.colPhoto')}</TableCell>
                <TableCell align="center">{t('worker:timeEntries.colStatus')}</TableCell>
                <TableCell align="right">{t('worker:timeEntries.colAction')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {days.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="caption" color="text.disabled">
                      {t('worker:timeEntries.noDaysInRange')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                days.map((d) => {
                  const chip = statusChip(d.status);
                  const isWeekend = d.weekday === 'Sat' || d.weekday === 'Sun';
                  const diffParts: string[] = [];
                  if (d.late_minutes > 0) diffParts.push(t('worker:timeEntries.diffLate', { m: d.late_minutes }));
                  if (d.early_leave_minutes > 0) diffParts.push(t('worker:timeEntries.diffEarly', { m: d.early_leave_minutes }));
                  return (
                    <TableRow
                      key={d.date}
                      hover
                      sx={{
                        bgcolor: d.is_holiday
                          ? 'rgba(156, 39, 176, 0.04)'
                          : d.is_rest_day
                            ? 'grey.50'
                            : undefined,
                      }}
                    >
                      <TableCell>
                        <Stack direction="column" spacing={0}>
                          <Typography variant="caption" sx={{ fontWeight: 500 }}>
                            {d.date}
                          </Typography>
                          <Typography variant="caption" color={isWeekend ? 'text.disabled' : 'text.secondary'} sx={{ fontSize: '0.65rem' }}>
                            {d.weekday}
                            {d.holiday_name ? ` · ${d.holiday_name}` : ''}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {d.scheduled_in && d.scheduled_out ? (
                          <Stack direction="column" spacing={0}>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                              {d.scheduled_in}–{d.scheduled_out}
                            </Typography>
                            {d.timetable_name && (
                              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }} noWrap>
                                {d.timetable_name}
                              </Typography>
                            )}
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {d.clock_in || d.clock_out ? (
                          <Stack direction="column" spacing={0}>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                              {fmtTime(d.clock_in)}–{fmtTime(d.clock_out)}
                            </Typography>
                            {d.total_hours != null && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                {Number(d.total_hours).toFixed(1)}h
                              </Typography>
                            )}
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {diffParts.length > 0 ? (
                          <Typography variant="caption" sx={{ color: 'warning.main' }}>
                            {diffParts.join(' · ')}
                          </Typography>
                        ) : d.missed_clock_in || d.missed_clock_out ? (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <ErrorOutlineOutlined sx={{ fontSize: 12, color: 'error.main' }} />
                            <Typography variant="caption" color="error.main">
                              {d.missed_clock_in && d.missed_clock_out
                                ? t('worker:timeEntries.missingBoth')
                                : d.missed_clock_in
                                  ? t('worker:timeEntries.missingIn')
                                  : t('worker:timeEntries.missingOut')}
                            </Typography>
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {/* Privacy audit Wave 5 — clicking the photo
                            chip opens a presigned-URL viewer. None
                            when the worker didn't clock in via mobile
                            (web punches don't capture photos) or the
                            org disabled photo capture. */}
                        {d.clock_in_photo_url ? (
                          <Tooltip title={t('worker:timeEntries.photoTitle')}>
                            <IconButton
                              size="small"
                              onClick={() => setPhotoDay(d)}
                              sx={{ color: 'primary.main' }}
                            >
                              <PhotoCameraOutlined sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={chip.labelKey ? t(`worker:timeEntries.${chip.labelKey}`) : '—'}
                          size="small"
                          color={chip.color}
                          variant="outlined"
                          sx={{ borderRadius: 1, height: 20, fontSize: '0.65rem' }}
                        />
                        {(d.deduction_days ?? 0) > 0 && (
                          <Tooltip
                            title={
                              d.deduction_reason
                                ? t(`worker:timeEntries.deductionReason.${d.deduction_reason}`)
                                : ''
                            }
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                mt: 0.25,
                                color: 'error.main',
                                fontWeight: 700,
                                fontSize: '0.6rem',
                              }}
                            >
                              {d.deduction_days === 0.5
                                ? t('worker:timeEntries.deductHalf')
                                : t('worker:timeEntries.deductFull')}
                            </Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {d.can_submit_missed_punch ? (
                          <Tooltip title={t('worker:timeEntries.missedTitle')}>
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              startIcon={<ScheduleOutlined sx={{ fontSize: 14 }} />}
                              onClick={() => openMissed(d)}
                              sx={{
                                borderRadius: 1, textTransform: 'none',
                                fontSize: '0.7rem', py: 0,
                              }}
                            >
                              {t('worker:timeEntries.submitBtn')}
                            </Button>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ADR-028 — Clock records (every raw punch across the selected range,
          grouped by day). Driven by the same presets as Timesheet. */}
      {view === 'events' && (
        <Box sx={{ width: '100%' }}>
          {punchesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6, width: '100%' }}>
              <CircularProgress size={28} />
            </Box>
          ) : punches.length === 0 ? (
            <Paper elevation={0} sx={{ width: '100%', p: 5, textAlign: 'center', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">
                {t('worker:timeEntries.noPunchesRange', { defaultValue: 'No clock records in this range.' })}
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={2} sx={{ width: '100%' }}>
              {groupPunchesByDay(punches).map((group) => (
                <Box key={group.day}>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.75 }}
                  >
                    {group.label}
                  </Typography>
                  <Stack spacing={1}>
                    {group.items.map((p) => {
                      const ts = new Date(p.punched_at);
                      const timeStr = ts.toLocaleTimeString(activeLocale(), { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                      return (
                        <Paper key={p.id} elevation={0} sx={{
                          width: '100%', p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider',
                          display: 'flex', alignItems: 'center', gap: 1.5, opacity: p.is_valid ? 1 : 0.7,
                        }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 84 }}>{timeStr}</Typography>
                          <Chip
                            label={p.is_valid
                              ? t('worker:timeEntries.punchValid', { defaultValue: 'Recorded' })
                              : t('worker:timeEntries.punchInvalid', { defaultValue: 'Not counted' })}
                            size="small"
                            color={p.is_valid ? 'success' : 'error'}
                            variant={p.is_valid ? 'filled' : 'outlined'}
                            sx={{ borderRadius: 1, height: 20, fontSize: '0.68rem' }}
                          />
                          {p.within_geofence != null && (
                            <Chip
                              label={p.within_geofence
                                ? t('worker:timeEntries.inZone', { defaultValue: 'In zone' })
                                : t('worker:timeEntries.outZone', { defaultValue: 'Out of zone' })}
                              size="small"
                              color={p.within_geofence ? 'success' : 'warning'}
                              variant="outlined"
                              sx={{ borderRadius: 1, height: 20, fontSize: '0.68rem' }}
                            />
                          )}
                          {p.address && (
                            <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {p.address}
                            </Typography>
                          )}
                          <Box sx={{ flexGrow: 1 }} />
                          {p.lat != null && p.lng != null && (
                            <Tooltip title={t('common:location.title')}>
                              <IconButton
                                size="small"
                                onClick={() => setLocPunch(p)}
                                sx={{ color: p.within_geofence === false ? 'warning.main' : 'primary.main' }}
                              >
                                <LocationOnOutlined sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {p.photo_url && (
                            <Avatar
                              variant="rounded"
                              src={p.photo_url ?? undefined}
                              onClick={() => p.photo_url && setPunchPhotoUrl(p.photo_url)}
                              sx={{ width: 32, height: 32, cursor: 'zoom-in', border: '1px solid', borderColor: 'divider' }}
                            />
                          )}
                        </Paper>
                      );
                    })}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      )}

      {/* Missed-clock-in submission — centered Dialog per svdy-ui §5
          (forms / mutations live in Dialog, not Drawer). */}
      <Dialog
        open={!!missedDay}
        onClose={() => !missedSubmitting && closeMissed()}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        {missedDay && (
          <>
            <DialogTitle sx={{ py: 2 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                  {t('worker:timeEntries.missedTitle')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {missedDay.date} · {missedDay.weekday}
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent dividers sx={{ py: 3 }}>
              <Stack spacing={2}>
                <Alert severity="info" variant="outlined" sx={{ fontSize: '0.8rem' }}>
                  {t('worker:timeEntries.dialogInfo')}
                </Alert>
                {missedError && (
                  <Alert
                    severity="error"
                    variant="outlined"
                    sx={{ fontSize: '0.8rem' }}
                    onClose={() => setMissedError(null)}
                  >
                    {missedError}
                  </Alert>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('worker:timeEntries.scheduledLabel')}
                  </Typography>
                  <Typography variant="body2">
                    {missedDay.scheduled_in && missedDay.scheduled_out
                      ? `${missedDay.scheduled_in} – ${missedDay.scheduled_out}`
                      : '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('worker:timeEntries.whatsMissing')}
                  </Typography>
                  <Typography variant="body2">
                    {missedDay.missed_clock_in && missedDay.missed_clock_out
                      ? t('worker:timeEntries.missingBothFull')
                      : missedDay.missed_clock_in
                        ? t('worker:timeEntries.missingInFull')
                        : t('worker:timeEntries.missingOutFull')}
                  </Typography>
                </Box>
                <FormControl size="small" fullWidth>
                  <InputLabel shrink>{t('worker:timeEntries.labelExpectedTime')}</InputLabel>
                  <TextField
                    type="time"
                    size="small"
                    value={missedTime}
                    onChange={(e) => setMissedTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    label={t('worker:timeEntries.labelExpectedTime')}
                  />
                </FormControl>
                <TextField
                  size="small"
                  fullWidth
                  required
                  multiline
                  rows={3}
                  label={t('worker:timeEntries.labelReason')}
                  placeholder={t('worker:timeEntries.placeholderReason')}
                  value={missedReason}
                  onChange={(e) => setMissedReason(e.target.value)}
                />
              </Stack>
            </DialogContent>
            <DialogActions sx={{ py: 2 }}>
              <Button
                onClick={closeMissed}
                disabled={missedSubmitting}
                sx={{ borderRadius: 1, textTransform: 'none' }}
              >
                {t('worker:timeEntries.cancel')}
              </Button>
              <Button
                variant="contained"
                onClick={submitMissed}
                disabled={missedSubmitting || !missedTime || !missedReason.trim()}
                sx={{ borderRadius: 1, textTransform: 'none' }}
              >
                {missedSubmitting ? <CircularProgress size={18} /> : t('worker:timeEntries.submitRequest')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setToast(null)} sx={{ width: '100%' }}>
          {toast}
        </Alert>
      </Snackbar>

      {/* Privacy audit Wave 5 — worker self-review photo dialog */}
      <PhotoViewerDialog
        open={!!photoDay}
        url={photoDay?.clock_in_photo_url}
        title={photoDay ? t('worker:timeEntries.photoCaption', { date: photoDay.date }) : undefined}
        subtitle={t('worker:timeEntries.photoSubtitle', { defaultValue: 'Captured at clock-in for attendance verification. Only your manager and HR can review it.' })}
        onClose={() => setPhotoDay(null)}
      />

      {/* ADR-028 — clock-records (punch) selfie viewer */}
      <PhotoViewerDialog
        open={!!punchPhotoUrl}
        url={punchPhotoUrl ?? undefined}
        onClose={() => setPunchPhotoUrl(null)}
      />

      {/* Location map popup — punch marker + geofence circle + zone verdict. */}
      <LocationViewerDialog
        open={!!locPunch}
        lat={locPunch?.lat}
        lng={locPunch?.lng}
        withinGeofence={locPunch?.within_geofence}
        address={locPunch?.address}
        accuracy={locPunch?.accuracy}
        fences={fences}
        subtitle={locPunch ? fmtLocalTime(locPunch.punched_at) : undefined}
        onClose={() => setLocPunch(null)}
      />
    </Box>
  );
};

export default WorkerTimeEntries;
